/**
 * LIVEL paštomatų pasirinkimas — Omniva (1 etapas).
 *
 * Atsakomybės:
 *  - gauti ir talpinti Omniva paštomatų sąrašą (viešas, be autentikacijos
 *    šaltinis, CORS leidžia tiesioginį fetch iš naršyklės);
 *  - leisti paiešką pagal miestą/pavadinimą;
 *  - išsaugoti pasirinkimą kaip cart attributes (matoma Shopify Admin
 *    užsakyme) ir kaip pasirinktą pristatymo adresą per Storefront API
 *    (cartDeliveryAddressesAdd, tokenless), kad checkout atsidarytų su
 *    užpildytu adresu;
 *  - parodyti orientacinę kainą / progresą iki nemokamo pristatymo, skaitant
 *    kainą ir ribą iš Shop metafields (irgi per tokenless Storefront API) —
 *    tai tik peržiūra: realią, patikimą kainą visada apskaičiuoja
 *    CarrierService serveryje checkout metu, ne šis skriptas.
 */

const OMNIVA_LOCATIONS_URL = 'https://www.omniva.ee/locations.json';
const OMNIVA_CACHE_KEY = 'livel_omniva_locations_lt_v1';
const OMNIVA_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h — viešas sąrašas keičiasi retai
const API_VERSION = '2025-10';
// Kaina/riba skaitoma iš šio Worker'io viešo /pricing endpoint'o (ne tiesiogiai
// iš Storefront API) — tokenless Storefront API neturi unauthenticated_read_metafields
// scope'o, todėl negali skaityti Shop metafields. Worker'is pats autentikuojasi
// per atskirą, minimalių teisių Admin API app'ą — žr. cf-worker/index.js komentarus.
const PRICING_ENDPOINT = 'https://livel-parcel-lockers.dianakatharina64.workers.dev/pricing';

/** @type {Array<Record<string, string>> | null} */
let lockersCache = null;
/** @type {Promise<Array<Record<string, string>>> | null} */
let lockersPromise = null;
/** @type {{ price: number | null, threshold: number | null } | null} */
let pricingCache = null;

function euros(cents) {
  return (cents / 100).toLocaleString('lt-LT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

async function fetchOmnivaLockers() {
  if (lockersCache) return lockersCache;
  if (lockersPromise) return lockersPromise;

  lockersPromise = (async () => {
    try {
      const raw = sessionStorage.getItem(OMNIVA_CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.ts && Date.now() - parsed.ts < OMNIVA_CACHE_TTL_MS && Array.isArray(parsed.data)) {
          lockersCache = parsed.data;
          return lockersCache;
        }
      }
    } catch (e) {
      // ignore corrupted cache
    }

    const res = await fetch(OMNIVA_LOCATIONS_URL);
    if (!res.ok) throw new Error('Omniva locations fetch failed: ' + res.status);
    const all = await res.json();
    const lt = all.filter((entry) => entry.A0_NAME === 'LT' && entry.TYPE === '0');

    try {
      sessionStorage.setItem(OMNIVA_CACHE_KEY, JSON.stringify({ ts: Date.now(), data: lt }));
    } catch (e) {
      // sessionStorage full/unavailable — proceed without caching
    }

    lockersCache = lt;
    return lockersCache;
  })();

  return lockersPromise;
}

function lockerAddress1(locker) {
  const street = [locker.A5_NAME, locker.A7_NAME].filter(Boolean).join(' ').trim();
  return street || locker.NAME || '';
}

function lockerCity(locker) {
  return locker.A3_NAME || locker.A2_NAME || locker.A1_NAME || '';
}

// Lietuviški vietovardžiai Omniva duomenyse pateikti linksniuotą forma (pvz.
// "Kauno m.", "Vilniaus m."), o pirkėjai natūraliai renka vardininką ("Kaunas",
// "Vilnius") — griežta substring paieška tokiu atveju nieko nerastų. Todėl
// papildomai lyginame žodžių PRADŽIĄ (šaknį): "kaun" atitinka ir "Kaunas", ir
// "Kauno". Taip pat nepaisome diakritikų (š, č, ž, ą, ę, ė, į, ų, ū), kad
// paieška veiktų net jei pirkėjas jų neįveda.
const SEARCH_STEM_LENGTH = 4;

function stripDiacritics(str) {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function normalizeForSearch(str) {
  return stripDiacritics(str.toLowerCase());
}

// Rikiavimo balai (didesnis = svarbesnis atitikimas), kad pvz. ieškant "Vilnius"
// tikri Vilniaus miesto paštomatai (A3_NAME/A2_NAME = "Vilniaus m.") būtų rodomi
// PRIEŠ paštomatus kituose miestuose, kurių tik GATVĖ vadinasi "Vilniaus g."
// (pvz. Anykščiuose, Nemenčinėje) — anksčiau šie dominuodavo rezultatuose vien
// dėl abėcėlinės failo tvarkos, nes paieška nebuvo rikiuojama pagal svarbą.
function matchScore(locker, q, qStem) {
  const city = normalizeForSearch(locker.A3_NAME || locker.A2_NAME || '');
  const cityWords = city.split(/[\s,.-]+/).filter(Boolean);
  if (cityWords.some((w) => w === q || w.slice(0, qStem.length) === qStem)) return 3;

  const name = normalizeForSearch(locker.NAME || '');
  if (name.startsWith(q) || name.split(/[\s,.-]+/).some((w) => w.length > 0 && w.slice(0, qStem.length) === qStem)) {
    return 2;
  }

  const haystack = normalizeForSearch([locker.NAME, locker.A2_NAME, locker.A3_NAME, locker.A1_NAME, locker.ZIP].join(' '));
  if (haystack.includes(q)) return 1;
  if (haystack.split(/[\s,.-]+/).some((word) => word.length > 0 && word.slice(0, qStem.length) === qStem)) return 0;
  return -1;
}

function searchLockers(lockers, query) {
  const qRaw = query.trim();
  if (qRaw.length < 2) return [];
  const q = normalizeForSearch(qRaw);
  const qStem = q.slice(0, Math.min(SEARCH_STEM_LENGTH, q.length));

  const scored = [];
  for (const l of lockers) {
    const score = matchScore(l, q, qStem);
    if (score >= 0) scored.push({ locker: l, score });
  }

  // Array.prototype.sort yra stabilus (ES2019+) — tos pačios reikšmės elementai
  // išlaiko originalią tvarką, tad rikiavimas tik sustiprina svarbą, nekeisdamas
  // esamos tvarkos to paties balo grupėje.
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 20).map((s) => s.locker);
}

async function getStorefrontCartId() {
  const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  const cart = await res.json();
  return { gid: `gid://shopify/Cart/${cart.token}`, cart };
}

async function storefrontQuery(query, variables) {
  const res = await fetch(`/api/${API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables })
  });
  return res.json();
}

async function getPricing() {
  if (pricingCache) return pricingCache;
  try {
    const res = await fetch(PRICING_ENDPOINT, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Pricing endpoint failed: ' + res.status);
    const data = await res.json();
    const price = Number.isFinite(data.price) ? data.price : parseFloat(data.price);
    const threshold = Number.isFinite(data.freeShippingThreshold)
      ? data.freeShippingThreshold
      : parseFloat(data.freeShippingThreshold);
    pricingCache = {
      price: Number.isFinite(price) ? Math.round(price * 100) : null,
      threshold: Number.isFinite(threshold) ? Math.round(threshold * 100) : null
    };
  } catch (e) {
    pricingCache = { price: null, threshold: null };
  }
  return pricingCache;
}

// LIVEL: Omniva ir LP EXPRESS widget'ai dalinasi TUOS PAČIUS livel_delivery_*
// cart attributes (vienu metu galimas tik vienas pasirinktas vežėjas). Po
// bet kokio sėkmingo pasirinkimo/išvalymo transliuojame ŠVIEŽIĄ cart.attributes
// būseną per CustomEvent, kad KITAS widget'as (kitame faile, cart-lpexpress.js)
// iš karto atsinaujintų — priešingu atveju, pvz., pasirinkus LP EXPRESS
// terminalą, Omniva widget'as (renderintas serverio su tais pačiais
// cart.attributes) klaidingai rodydavo save kaip "pasirinktą", nes anksčiau
// tikrindavo tik locker pavadinimo buvimą, ne carrier reikšmę. Abu failai
// turi tapatų klausytoją apačioje, tik skirtingą tikrinamą carrier reikšmę.
// 2026-09-08 UI/UX pertvarka: detail dabar papildomai neša visą švieziai
// gautą `cart` objektą, kad assets/cart-delivery.js (bendro pristatymo bloko
// orkestratorius) galėtų perskaičiuoti VIENĄ bendrą "iki nemokamo pristatymo"
// pranešimą be papildomo /cart.js fetch — pati kainos/ribos logika nepaliesta.
function broadcastDeliverySelection(cart) {
  const attrs = (cart && cart.attributes) || {};
  document.dispatchEvent(new CustomEvent('livel:delivery-selection-changed', {
    detail: {
      carrier: attrs.livel_delivery_carrier || '',
      name: attrs.livel_delivery_locker_name || '',
      address: attrs.livel_delivery_locker_address || '',
      city: attrs.livel_delivery_locker_city || '',
      zip: attrs.livel_delivery_locker_zip || '',
      cart: cart || null
    }
  }));
}

// LIVEL: po adreso pre-fill'o (aukščiau) papildomai priverstinai pažymime
// TEISINGĄ checkout siuntimo būdo (shipping method) parinktį per Storefront
// API cartSelectedDeliveryOptionsUpdate — anksčiau Shopify pati pasirinkdavo
// numatytąją parinktį (kartais tai būdavo "Kurjeris", ne realiai pasirinktas
// paštomato vežėjas), o klientas turėdavo pats rankiniu būdu perjungti
// teisingą radio checkout'e. HANDLE reikšmės NIEKADA nehardcodinamos — jos
// visada randamos DINAMIŠKAI iš dabartinio cart.deliveryGroups sąrašo,
// lyginant delivery option title su vežėjo pavadinimu (raktažodžiais).
// Neliečia kainų/pricing/free-shipping skaičiavimo — tos parinktys jau
// egzistuoja su savo estimatedCost, apskaičiuotu CarrierService/statinio
// tarifo serveryje; ši mutacija tik PASIRENKA vieną iš jau esančių.
async function setSelectedDeliveryOption(cartGid, titleKeywords) {
  try {
    const groupsResult = await storefrontQuery(
      `query LivelGetDeliveryGroups($cartId: ID!) {
        cart(id: $cartId) {
          deliveryGroups(first: 10) {
            edges { node { id deliveryOptions { handle title } } }
          }
        }
      }`,
      { cartId: cartGid }
    );
    const edges =
      (groupsResult.data &&
        groupsResult.data.cart &&
        groupsResult.data.cart.deliveryGroups &&
        groupsResult.data.cart.deliveryGroups.edges) ||
      [];

    let deliveryGroupId = null;
    let deliveryOptionHandle = null;
    for (const edge of edges) {
      const group = edge.node;
      const match = group.deliveryOptions.find((option) => {
        const title = (option.title || '').toLowerCase();
        return titleKeywords.some((keyword) => title.includes(keyword));
      });
      if (match) {
        deliveryGroupId = group.id;
        deliveryOptionHandle = match.handle;
        break;
      }
    }

    // Jei atitinkamos parinkties nerasta (pvz., vežėjas šiuo metu neturi
    // aktyvaus tarifo tam regionui) — tyliai praleidžiame, nekeičiame
    // esamos checkout būsenos. Cart attributes išlieka teisingi bet kuriuo
    // atveju (merchant-visible pasirinkimas nepriklauso nuo šios mutacijos).
    if (!deliveryGroupId || !deliveryOptionHandle) return;

    const updateResult = await storefrontQuery(
      `mutation LivelSetDeliveryOption($cartId: ID!, $selectedDeliveryOptions: [CartSelectedDeliveryOptionInput!]!) {
        cartSelectedDeliveryOptionsUpdate(cartId: $cartId, selectedDeliveryOptions: $selectedDeliveryOptions) {
          userErrors { field message code }
          warnings { message code }
        }
      }`,
      {
        cartId: cartGid,
        selectedDeliveryOptions: [{ deliveryGroupId, deliveryOptionHandle }]
      }
    );
    const payload = updateResult.data && updateResult.data.cartSelectedDeliveryOptionsUpdate;
    if (payload && ((payload.userErrors && payload.userErrors.length) || (payload.warnings && payload.warnings.length))) {
      // eslint-disable-next-line no-console
      console.warn('LIVEL cartSelectedDeliveryOptionsUpdate:', payload.userErrors, payload.warnings);
    }
  } catch (e) {
    // Convenience — cart attributes jau užtikrina merchant-visible
    // pasirinkimą; jei ši mutacija nepavyksta, klientas tiesiog turi pats
    // rankiniu būdu pasirinkti teisingą radio checkout'e (ankstesnė elgsena).
  }
}

async function saveSelection(root, locker) {
  const address1 = lockerAddress1(locker);
  const city = lockerCity(locker);
  const zip = locker.ZIP || '';
  const address2 = `${root.dataset.carrierLabel} — ${locker.NAME}`;

  setLoading(root, true);

  // 1) Cart attributes — visible in Shopify Admin order, order history, and
  //    (with a small Notifications template edit) confirmation emails.
  const updateRes = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: {
        livel_delivery_carrier: 'Omniva',
        livel_delivery_locker_name: locker.NAME || '',
        livel_delivery_locker_address: address1,
        livel_delivery_locker_city: city,
        livel_delivery_locker_zip: zip
      }
    })
  });
  const cart = await updateRes.json();

  // 2) Storefront API — pre-fill the checkout delivery address with the
  //    locker's address, on the SAME cart (tokenless, no access token).
  const cartGid = `gid://shopify/Cart/${cart.token}`;
  try {
    await storefrontQuery(
      `mutation LivelSetAddress($cartId: ID!, $addresses: [CartSelectableAddressInput!]!) {
        cartDeliveryAddressesAdd(cartId: $cartId, addresses: $addresses) {
          userErrors { field message }
        }
      }`,
      {
        cartId: cartGid,
        addresses: [
          {
            address: {
              deliveryAddress: {
                address1,
                address2,
                city,
                zip,
                countryCode: 'LT'
              }
            },
            selected: true,
            oneTimeUse: true
          }
        ]
      }
    );
  } catch (e) {
    // Address prefill is a convenience, not a hard requirement — if it fails,
    // the cart attributes above still preserve the merchant-visible selection
    // and the customer can still enter the address manually at checkout.
  }

  // 3) Priverstinai pažymime teisingą checkout shipping method — žr.
  //    setSelectedDeliveryOption() komentarą aukščiau.
  await setSelectedDeliveryOption(cartGid, ['omniva']);

  setLoading(root, false);
  applySelectedState(root, { name: locker.NAME || '', address: address1, city, zip });
  broadcastDeliverySelection(cart);
  await refreshFreeShippingMessage(root, cart);
}

async function clearSelection(root) {
  setLoading(root, true);
  const updateRes = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: {
        livel_delivery_carrier: '',
        livel_delivery_locker_name: '',
        livel_delivery_locker_address: '',
        livel_delivery_locker_city: '',
        livel_delivery_locker_zip: ''
      }
    })
  });
  const cart = await updateRes.json();
  setLoading(root, false);
  applyIdleState(root);
  broadcastDeliverySelection(cart);
  await refreshFreeShippingMessage(root, cart);
}

function setLoading(root, isLoading) {
  const loadingEl = root.querySelector('[data-cart-omniva-loading]');
  if (loadingEl) loadingEl.hidden = !isLoading;
}

function applySelectedState(root, selection) {
  root.querySelector('[data-cart-omniva-idle]').hidden = true;
  const selectedEl = root.querySelector('[data-cart-omniva-selected]');
  selectedEl.hidden = false;
  root.querySelector('[data-cart-omniva-selected-name]').textContent = selection.name;
  root.querySelector('[data-cart-omniva-selected-address]').textContent =
    [selection.address, selection.city, selection.zip].filter(Boolean).join(', ');
}

function applyIdleState(root) {
  root.querySelector('[data-cart-omniva-selected]').hidden = true;
  root.querySelector('[data-cart-omniva-idle]').hidden = false;
  const searchInput = root.querySelector('[data-cart-omniva-search]');
  if (searchInput) searchInput.value = '';
  const resultsEl = root.querySelector('[data-cart-omniva-results]');
  if (resultsEl) {
    resultsEl.hidden = true;
    resultsEl.innerHTML = '';
  }
}

function renderResults(root, results) {
  const resultsEl = root.querySelector('[data-cart-omniva-results]');
  if (!resultsEl) return;

  if (results.length === 0) {
    resultsEl.hidden = false;
    resultsEl.innerHTML = `<li class="cart-omniva__result cart-omniva__result--empty">${root.dataset.noResultsText}</li>`;
    return;
  }

  resultsEl.hidden = false;
  resultsEl.innerHTML = '';
  for (const locker of results) {
    const li = document.createElement('li');
    li.className = 'cart-omniva__result';

    // Visa eilutė yra vienas <button> — paspaudus bet kur (pavadinimą ar
    // adresą) paštomatas iškart pasirenkamas, jokio atskiro "Pasirinkti"
    // mygtuko nebereikia. <button> duoda hover/focus-visible ir klaviatūros
    // pasiekiamumą "nemokamai".
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'cart-omniva__result-button';
    button.addEventListener('click', () => saveSelection(root, locker));

    const name = document.createElement('span');
    name.className = 'cart-omniva__result-name';
    name.textContent = locker.NAME || '';

    const address = document.createElement('span');
    address.className = 'cart-omniva__result-address';
    address.textContent = [lockerAddress1(locker), lockerCity(locker)].filter(Boolean).join(', ');

    button.append(name, address);
    li.append(button);
    resultsEl.appendChild(li);
  }
}

async function refreshFreeShippingMessage(root, cartMaybe) {
  const messageEl = root.querySelector('[data-cart-omniva-free-shipping]');
  if (!messageEl) return;

  const pricing = await getPricing();
  if (pricing.threshold == null) {
    messageEl.textContent = '';
    return;
  }

  let cart = cartMaybe;
  if (!cart) {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    cart = await res.json();
  }

  // total_price = po nuolaidos kodų — taip pat skaičiuoja Shopify free-shipping nuolaida (nuo 30 €).
  const subtotal = cart.total_price;

  if (subtotal >= pricing.threshold) {
    messageEl.textContent = root.dataset.freeShippingReachedText;
  } else if (pricing.price != null) {
    const remaining = pricing.threshold - subtotal;
    messageEl.textContent = root.dataset.freeShippingProgressText.replace('{amount}', euros(remaining));
  } else {
    messageEl.textContent = '';
  }
}

function wireSearch(root) {
  const input = root.querySelector('[data-cart-omniva-search]');
  const hint = root.querySelector('[data-cart-omniva-hint]');
  if (!input) return;

  let debounceTimer = null;

  input.addEventListener('input', () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(async () => {
      const query = input.value;
      if (query.trim().length < 2) {
        const resultsEl = root.querySelector('[data-cart-omniva-results]');
        if (resultsEl) {
          resultsEl.hidden = true;
          resultsEl.innerHTML = '';
        }
        if (hint) hint.hidden = false;
        return;
      }
      if (hint) hint.hidden = true;
      setLoading(root, true);
      try {
        const lockers = await fetchOmnivaLockers();
        renderResults(root, searchLockers(lockers, query));
      } catch (e) {
        renderResults(root, []);
      } finally {
        setLoading(root, false);
      }
    }, 250);
  });
}

function wireChangeButton(root) {
  const button = root.querySelector('[data-cart-omniva-change]');
  if (!button) return;
  button.addEventListener('click', () => clearSelection(root));
}

function initCartOmniva(root) {
  if (root.dataset.cartOmnivaInitialized) return;
  root.dataset.cartOmnivaInitialized = 'true';

  wireSearch(root);
  wireChangeButton(root);

  // LIVEL: carrier patikrinimas ČIA būtinas nuo tada, kai atsirado LP EXPRESS
  // (žr. broadcastDeliverySelection() komentarą aukščiau) — anksčiau, kai
  // egzistavo tik Omniva, pakako patikrinti vien locker pavadinimo buvimą.
  // Dabar abu widget'ai serverio renderinami su tais pačiais bendrais
  // cart.attributes, tad be šio patikrinimo Omniva klaidingai rodydavosi
  // "pasirinktas", kai realiai buvo pasirinktas LP EXPRESS.
  if (root.dataset.selectedLockerName && root.dataset.selectedCarrier === 'Omniva') {
    applySelectedState(root, {
      name: root.dataset.selectedLockerName,
      address: root.dataset.selectedLockerAddress || '',
      city: root.dataset.selectedLockerCity || '',
      zip: root.dataset.selectedLockerZip || ''
    });
  } else {
    applyIdleState(root);
  }

  refreshFreeShippingMessage(root);
}

function initAll() {
  document.querySelectorAll('[data-cart-omniva]').forEach(initCartOmniva);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

// Re-init after Shopify Section Rendering API swaps (cart drawer / cart page
// re-renders after quantity changes etc.) so the widget keeps working.
document.addEventListener('shopify:section:load', initAll);

// Re-init whenever a NEW, not-yet-wired [data-cart-omniva] element appears
// anywhere in the DOM. Needed specifically for the drawer's own widget
// instance: when the cart is empty on page load, the drawer's
// cart-omniva-widget markup doesn't exist yet, so DOMContentLoaded's
// initAll() never finds it — it only enters the DOM later (first add-to-cart)
// via the theme's own cart-update rendering, which replaces the drawer's
// inner content wholesale rather than patching it in place, so a freshly
// swapped-in widget always starts unwired. Tried listening for the theme's
// `shopify:cart:lines-update` event (dispatched by this store's
// cart-drawer.js, confirmed via StandardEvents.cartLinesUpdate) and awaiting
// its event.promise first — but that promise resolves BEFORE the DOM swap
// actually lands (confirmed empirically: the widget was still the OLD,
// already-initialized element right when the promise resolved), so initAll()
// ran too early and missed the new element. A MutationObserver sidesteps
// that timing entirely — it fires exactly when the new element is actually
// in the DOM, regardless of which internal mechanism (view transition,
// section rendering, etc.) put it there.
const cartOmnivaObserver = new MutationObserver(() => {
  const unwired = document.querySelector('[data-cart-omniva]:not([data-cart-omniva-initialized])');
  if (unwired) initAll();
});
cartOmnivaObserver.observe(document.body, { childList: true, subtree: true });

// Sinchronizacija su LP EXPRESS widget'u — žr. broadcastDeliverySelection()
// komentarą aukščiau. Klausomasi VISŲ [data-cart-omniva] instancijų (drawer
// ir /cart puslapio widget'ai niekada abu neegzistuoja tame pačiame puslapyje
// šioje temoje, bet saugu apdoroti visus, jei tai pasikeistų).
document.addEventListener('livel:delivery-selection-changed', (event) => {
  const detail = event.detail || {};
  document.querySelectorAll('[data-cart-omniva]').forEach((root) => {
    if (detail.carrier === 'Omniva' && detail.name) {
      applySelectedState(root, { name: detail.name, address: detail.address, city: detail.city, zip: detail.zip });
    } else {
      applyIdleState(root);
    }
  });
});
